import React from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {MenuItem} from '@components';

export interface IMenuItem {
  name: string;
  path?: string;
  icon?: string;
  children?: Array<IMenuItem>;
  permitted?: boolean;
  disabled: boolean;
}

const MenuSidebar = () => {
  const user = useSelector((state: any) => state.auth.currentUser);
  const role = useSelector((state: any) => state.auth.selectedRole?.name);
  const roleSlug = useSelector((state: any) => state.auth.selectedRole?.slug);
  const sidebarSkin = useSelector((state: any) => state.ui.sidebarSkin);
  const menuItemFlat = useSelector((state: any) => state.ui.menuItemFlat);
  const menuChildIndent = useSelector((state: any) => state.ui.menuChildIndent);
  const permissions = useSelector((state: any) => state.auth.permissions);
  
  const MENU: IMenuItem[] = [
    {
      name: 'menusidebar.label.dashboard',
      path: '/',
      icon: 'fas fa-tachometer-alt',
      permitted: permissions?.dashboard?.view,
      disabled: false
    },
    {
      name: 'Manage Users',
      path: '/users',
      icon: 'fas fa-users',
      permitted: permissions?.users?.list,
      disabled: false
    },
    {
      name: 'Manage Roles',
      path: '/roles',
      icon: 'fas fa-user-tag',
      permitted: permissions?.roles?.list,
      disabled: false
    },
    {
      name: 'Manage Permissions',
      path: '/permissions',
      icon: 'fas fa-user-shield',
      permitted: false, //permissions?.permissions?.list
      disabled: false
    },
    {
      name: 'Manage Franchises',
      icon: 'fas fa-briefcase',
      permitted: permissions?.franchise?.list,
      disabled: false,
      children: [
        {
          name: 'Franchise List',
          path: '/franchise',
          permitted: permissions?.franchise?.list,
          disabled: false
        },
        {
          name: 'Franchise Notes',
          path: '/franchise-notes',
          permitted: permissions?.notes?.['all-franchise-notes'],
          disabled: false
        }
      ]
    },
    {
      name: 'Manage Postcodes',
      path: '/postcodes',
      icon: 'fas fa-envelope',
      permitted: permissions?.postcodes?.list,
      disabled: false
    },
    {
      name: 'Manage Centres',
      path: '/centers',
      icon: 'fas fa-code-branch',
      permitted: permissions?.centre?.list,
      disabled: false
    },
    {
      name: 'Student Subject Count',
      permitted: permissions?.['student-count']?.['list-student-count'],
      icon: 'fas fa-book-reader',
      children: [
        // {
        //   name: 'Submit Student Count',
        //   path: '/submit-student-counts',
        //   permitted: permissions?.['student-count']?.['create-student-count'],
        //   disabled: false
        // },
        {
          name: 'Student Subject Counts',
          path: '/student-counts',
          permitted: permissions?.['student-count']?.['list-student-count'],
          disabled: false
        },
        {
          name: 'Transactions',
          path: '/student-count-transactions',
          permitted: permissions?.['student-count']?.['list-student-count'],
          disabled: false
        }
      ],
      disabled: false
    },
    {
      name: 'Material Orders',
      permitted: permissions?.orders?.list,
      icon: 'fas fa-shopping-cart',
      children: [
        {
          name: 'Place New Order',
          path: '/orders/place',
          permitted: permissions?.orders?.place,
          disabled: false
        },
        {
          name: 'In-Progress',
          path: '/in-progress-orders',
          permitted: permissions?.orders?.list,
          disabled: false
        },
        {
          name: 'Dispatched/Cancelled',
          path: '/dispatched-cancelled-orders',
          permitted: permissions?.orders?.list,
          disabled: false
        },
        {
          name: 'Restricted Franchise Cart',
          path: '/restricted-franchise-carts',
          permitted: permissions?.orders?.['restricted-franchise-carts'],
          disabled: false
        }
      ],
      disabled: false
    },
    {
      name: 'Manage Students',
      permitted: permissions?.student?.list,
      icon: 'fas fa-user-graduate',
      children: [
        {
          name: 'Student List',
          path: '/students',
          permitted: permissions?.student?.list,
          disabled: false
        },
        {
          name: 'Parent List',
          path: '/parents',
          permitted: permissions?.student?.list,
          disabled: false
        }
      ],
      disabled: false
    },
    {
      name: 'Manage Invoices',
      permitted: permissions?.student?.invoices,
      icon: 'fas fa-file-invoice-dollar',
      children: [
        {
          name: 'Student Invoices',
          path: '/student-invoices',
          permitted: permissions?.student?.invoices,
          disabled: false
        },
        {
          name: 'Preview Invoices',
          path: '/preview-invoices',
          permitted: permissions?.student?.['preview-invoices'],
          disabled: false
        }
      ],
      disabled: false
    },
    {
      name: 'Manage Leads',
      icon: 'fas fa-headset',
      permitted: permissions?.lead?.list,
      disabled: false,
      children: [
        {
          name: 'Leads List',
          path: '/leads',
          permitted: permissions?.lead?.list,
          disabled: false
        },
        {
          name: 'Prospective Students',
          path: '/prospective-students',
          permitted: permissions?.lead?.['prospective-students'],
          disabled: false
        }
      ],
    },
    {
      name: 'Print Summary',
      icon: 'fas fa-chart-line',
      permitted: permissions?.['print-summary']?.list || permissions?.['print-summary']?.['exceeded-threshold'],
      disabled: false,
      children: [
        {
          name: 'Print History',
          path: '/print-summary',
          permitted: permissions?.['print-summary']?.list,
          disabled: false
        },
        {
          name: 'Exceeded Threshold',
          path: '/exceeded-print-threshold',
          permitted: permissions?.['print-summary']?.['exceeded-threshold'],
          disabled: false
        }
      ]
    },
    {
      name: 'Reports',
      path: '/reports',
      icon: 'fas fa-chart-pie',
      // Single entry by design — the index page at /reports is the navigation for
      // all 14 reports, and it explains what each one answers. Nesting them here
      // would be a 14-item wall that says less.
      // Gated on the pre-existing 'report' role module (role_modules id 36),
      // whose 'view' permission (id 181) is what the old disabled placeholder
      // used. Singular, not 'reports' — grant report:view on the role-permission
      // screen to give a role access. Super Admin needs no grant.
      permitted: permissions?.report?.view,
      disabled: false
    },
    {
      name: 'Learning Pathways',
      path: '/learning-pathway',
      icon: 'fas fa-bezier-curve',
      permitted: permissions?.['learning-pathway']?.list,
      disabled: false
    },
    {
      name: 'Setting Patterns',
      path: '/setting-patterns',
      icon: 'fas fa-layer-group',
      permitted: permissions?.['setting-pattern']?.list,
      disabled: false
    },
    {
      name: 'Ops Manual',
      path: '/ops-manual',
      icon: 'fas fa-folder',
      permitted: permissions?.opsmanual?.list,
      disabled: false
    },
    // {
    //   name: 'Ops Manual New',
    //   path:  '/ops-manual-new',
    //   icon: 'fas fa-folder',
    //   permitted: permissions?.opsmanual?.list,
    //   disabled: false
    // },
    // {
    //   name: 'Ops Manual Dropbox',
    //   path:  '/ops-manual-dropbox',
    //   icon: 'fas fa-folder',
    //   permitted: permissions?.opsmanual?.list,
    //   disabled: false
    // },
    {
      name: 'AI DB Assistant',
      path: '/ai-db-assistant',
      icon: 'fas fa-robot',
      permitted: permissions?.['ai-db-assistant']?.menu,
      disabled: false
    },
    {
      name: 'Auto Marking',
      path: '/auto-marking',
      icon: 'fas fa-robot',
      permitted: permissions?.['auto-mark']?.menu,
      disabled: false
    },
    {
      name: 'Session Notes',
      path: '/session-notes',
      icon: 'fas fa-clipboard-list',
      permitted: permissions?.['session-notes']?.list,
      disabled: false
    },
    {
      name: 'Auto Marking',
      path: '/auto-marking',
      icon: 'fas fa-robot',
      permitted: permissions?.['auto-mark']?.menu,
      disabled: false
    },
    {
      name: 'Masters',
      permitted: permissions?.masters?.list || permissions?.subjects?.list || permissions?.modules?.list || permissions?.sets?.list || permissions?.blocks?.list || permissions?.handbooks?.list || permissions?.['other-items']?.list || permissions?.['pricing-plans']?.list || permissions?.['camps']?.list,
      icon: 'fas fa-circle',
      disabled: false,
      children: [
        {
          name: 'Regions',
          path: '/regions',
          permitted: permissions?.masters?.region,
          disabled: false
        },
        {
          name: 'Areas',
          path: '/areas',
          permitted: permissions?.masters?.area,
          disabled: false
        },
        {
          name: 'Manage Subjects',
          path: '/subjects',
          permitted: permissions?.subjects?.list,
          disabled: false
        },
        {
          name: 'Manage Modules',
          path: '/modules',
          permitted: permissions?.modules?.list,
          disabled: false
        },
        {
          name: 'Manage Sets',
          path: '/sets',
          permitted: permissions?.sets?.list,
          disabled: false
        },
        {
          name: 'Manage Blocks',
          path: '/blocks',
          permitted: permissions?.blocks?.list,
          disabled: false
        },
        {
          name: 'Manage Handbooks',
          path: '/handbooks',
          permitted: permissions?.handbooks?.list,
          disabled: false
        },
        {
          name: 'Other Items',
          path: '/other-items',
          permitted: permissions?.['other-items']?.list,
          disabled: false
        },
        {
          name: 'Pricing Plans',
          path: '/pricing-plans',
          permitted: permissions?.['pricing-plans']?.list,
          disabled: false
        },
        {
          name: 'Camps',
          path: '/camps',
          permitted: permissions?.['camps']?.list,
          disabled: false
        }
      ],
    },
    {
      name: 'Manage Settings',
      path: '/settings',
      icon: 'fas fa-cog',
      permitted: permissions?.setting?.list,
      disabled: false
    },
    {
      name: 'Marketing Shop',
      path: 'https://proco.infigosoftware.com/firstclasslearning/login',
      icon: 'fas fa-store',
      permitted: permissions?.['marketing-shop']?.link,
      disabled: false
    },
    {    
      name: 'Sales Training Portal',
      path: 'https://first-class-learning.mn.co/sign_in',
      icon: 'fas fa-video',
      permitted: permissions?.['sales-training']?.link,
      disabled: false
    },
    {
      name: 'Student Work',
      permitted: permissions?.['student-work']?.view,
      icon: 'fas fa-school',
      disabled: false,
      children: [
        {
          name: 'Work Summary',
          path: '/work-summary',
          permitted: permissions?.['student-work']?.summary,
          disabled: false
        },
  
        {
          name: 'Work History',
          path: '/work-history',
          permitted: permissions?.['student-work']?.history,
          disabled: false
        },
        {
          name: 'Session Notes',
          path: '/student-session-notes',
          permitted: permissions?.['student-work']?.['session-notes'],
          disabled: false
        }
      ],
    },
  ];

  /**
   * Super Admin is not gated.
   *
   * Every item above is gated on a specific module and action, so a module with
   * no permission rows is missing from the compact permissions payload entirely
   * (it is built with an INNER JOIN from role_modules to permissions) and its
   * item silently disappears — for Super Admin too. That is how Reports vanished.
   * An item hardcoded to permitted: false disappears the same way.
   *
   * Rather than patch each case, Super Admin bypasses the check outright, so any
   * module added later that has no permission rows yet still shows up here.
   *
   * Children are forced as well: MenuItem gates each child on its own
   * `permitted`, so overriding only the parent would render an empty submenu.
   */
  const isSuperAdmin = roleSlug === 'super-admin';

  const unblock = (items: IMenuItem[]): IMenuItem[] =>
    items.map((item) => ({
      ...item,
      permitted: true,
      ...(item.children ? {children: unblock(item.children)} : {})
    }));

  const VISIBLE_MENU = isSuperAdmin ? unblock(MENU) : MENU;

  return (
    <aside className={`main-sidebar elevation-4 ${sidebarSkin}`}>
      <Link to="/" className="brand-link">
        <img
          src={process.env.REACT_APP_ENV !== 'production' ? "/img/FCL-Landscape.png" : "/img/FCL-Landscape-White.png"}
          alt="FCL Logo"
          className="brand-image"
          style={{opacity: '.8'}}
        />
        <span className="brand-text font-weight-light"> {process.env.REACT_APP_ENV === 'production' ? "FCL" : "UAT"}</span>
      </Link>
      <div className="sidebar">
        <div className="user-panel mt-3 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src={user.picture || '/img/default-profile.png'}
              className="img-circle elevation-2"
              alt="User"
            />
          </div>
          <div className="info">
            <Link to="/profile" className="d-block">
              {user.first_name +' '+ (user.last_name ?? '')}
            </Link>
            <div className={process.env.REACT_APP_ENV === 'production' ? "text-white-50" : "text-black-50"}> { role } </div>
          </div>
        </div>
        <nav className="mt-2" style={{overflowY: 'hidden'}}>
          <ul
            className={`nav nav-pills nav-sidebar flex-column${
              menuItemFlat ? ' nav-flat' : ''
            }${menuChildIndent ? ' nav-child-indent' : ''}`}
            role="menu"
          >
            {VISIBLE_MENU.map((menuItem: IMenuItem) => (
              menuItem?.permitted ? <MenuItem key={menuItem.name} menuItem={menuItem} /> : ''
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default MenuSidebar;

